import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchDispensaryPosTrends,
  formatCentsCompact,
  formatHourLabel,
  formatHourRange,
  type PosTransactionTrends,
} from "../lib/dispensary-trends-api";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

const WINDOWS = [30, 90] as const;
type WindowDays = (typeof WINDOWS)[number];

const CARD =
  "overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]";

function Stat({
  label,
  value,
  sub,
  emphasis,
}: {
  label: string;
  value: string;
  sub?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-0 px-4 py-3">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-[#888888]">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${emphasis ? "text-green" : "text-[#111111]"}`}>{value}</p>
      {sub ? <p className="mt-0.5 truncate text-xs text-[#666666]">{sub}</p> : null}
    </div>
  );
}

/**
 * Hour-of-day columns. Only hours the store actually transacts in are drawn —
 * a fixed 0-23 axis would spend two thirds of its width on empty overnight
 * columns and flatten the curve that matters.
 */
function HourCurve({ trends }: { trends: PosTransactionTrends }) {
  const active = trends.byHour.filter((h) => h.transactions > 0);
  if (active.length === 0) return null;

  const first = active[0]!.hour;
  const last = active[active.length - 1]!.hour;
  const shown = trends.byHour.filter((h) => h.hour >= first && h.hour <= last);
  const max = Math.max(...shown.map((h) => h.deliveries), 1);
  const peakHour = trends.peak.hour;

  return (
    <div>
      <div className="flex items-end gap-1" style={{ height: 132 }}>
        {shown.map((h) => {
          const pct = (h.deliveries / max) * 100;
          const isPeak = h.hour === peakHour;
          return (
            <div key={h.hour} className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end">
              <span className="mb-1 text-[10px] font-medium text-[#888888] opacity-0 transition-opacity group-hover:opacity-100">
                {h.deliveries}
              </span>
              <div
                className={`w-full rounded-t ${isPeak ? "bg-green" : "bg-[#CFE3DE]"}`}
                style={{ height: `${Math.max(pct, h.deliveries > 0 ? 3 : 0)}%` }}
                title={`${formatHourRange(h.hour)} — ${h.deliveries} deliveries`}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-1">
        {shown.map((h) => (
          <div key={h.hour} className="min-w-0 flex-1 text-center">
            {/* Label every third hour: 13 columns of text would collide. */}
            <span className="text-[10px] text-[#999999]">
              {h.hour % 3 === 0 ? formatHourLabel(h.hour).replace(" ", "") : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DayOfWeekBars({ trends }: { trends: PosTransactionTrends }) {
  const max = Math.max(...trends.byDayOfWeek.map((d) => d.deliveries), 1);
  return (
    <div className="space-y-2">
      {trends.byDayOfWeek.map((d) => (
        <div key={d.dow} className="flex items-center gap-3">
          <span className="w-8 flex-shrink-0 text-xs font-medium text-[#666666]">{d.label}</span>
          <div className="h-4 min-w-0 flex-1 overflow-hidden rounded bg-[#F4F4F4]">
            <div
              className="h-full rounded bg-green"
              style={{ width: `${(d.deliveries / max) * 100}%` }}
            />
          </div>
          <span className="w-20 flex-shrink-0 text-right text-xs text-[#666666]">
            {d.deliveries} · {d.deliveriesPerDay}/day
          </span>
        </div>
      ))}
    </div>
  );
}

/** Daily delivery volume as a sparkline — the "is this growing" question. */
function DailySparkline({ trends }: { trends: PosTransactionTrends }) {
  const days = trends.byDay;
  if (days.length < 2) return null;

  const max = Math.max(...days.map((d) => d.deliveries), 1);
  const w = 100;
  const h = 28;
  const points = days
    .map((d, i) => {
      const x = (i / (days.length - 1)) * w;
      const y = h - (d.deliveries / max) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-8 w-full" aria-hidden="true">
      <polyline points={points} fill="none" stroke="#1F7A6B" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function DeliveryTrendsCard() {
  const { getToken, dispensaryId } = useDispensaryWorkspace();
  const [days, setDays] = useState<WindowDays>(90);

  const { data: trends, isLoading } = useQuery({
    queryKey: ["dispensary", "pos-trends", dispensaryId, days],
    enabled: dispensaryId != null,
    queryFn: async () => fetchDispensaryPosTrends(getToken, dispensaryId!, days),
    staleTime: 5 * 60_000,
  });

  // Per-store only: the endpoint is dispensary-scoped, and summing POS history
  // across locations would mix stores with very different footprints.
  if (dispensaryId == null) return null;

  if (isLoading) {
    return (
      <div className={`${CARD} h-[420px] animate-pulse`}>
        <div className="border-b border-[#F0F0F0] px-4 py-4">
          <div className="h-4 w-32 rounded bg-[#F0F0F0]" />
        </div>
      </div>
    );
  }

  if (!trends || trends.totals.transactions === 0) {
    return (
      <div className={CARD}>
        <div className="flex items-center justify-between border-b border-[#F0F0F0] px-4 py-4">
          <span className="text-sm font-bold text-[#111111]">Delivery Trends</span>
        </div>
        <div className="px-4 py-10 text-center">
          <p className="text-sm font-medium text-[#111111]">No POS history yet</p>
          <p className="mt-2 text-sm text-[#666666]">
            Trends appear once this location&rsquo;s POS transactions have synced.
          </p>
        </div>
      </div>
    );
  }

  const t = trends.totals;
  const peak = trends.peak;

  return (
    <div className={CARD}>
      <div className="flex items-center justify-between border-b border-[#F0F0F0] px-4 py-4">
        <div className="min-w-0">
          <span className="text-sm font-bold text-[#111111]">Delivery Trends</span>
          <span className="ml-2 text-xs text-[#888888]">
            {t.activeDays} days · store time
          </span>
        </div>
        <div className="flex flex-shrink-0 gap-1 rounded-lg bg-[#F4F4F4] p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDays(w)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                days === w ? "bg-white text-[#111111] shadow-sm" : "text-[#777777] hover:text-[#111111]"
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 divide-x divide-[#F0F0F0] border-b border-[#F0F0F0] sm:grid-cols-4">
        <Stat
          label="Deliveries"
          value={t.deliveries.toLocaleString("en-US")}
          sub={`${t.deliveriesPerDay}/day avg`}
        />
        <Stat label="% of orders" value={`${t.deliveryPct}%`} sub={`of ${t.transactions.toLocaleString("en-US")} total`} />
        <Stat
          label="% of revenue"
          value={`${t.deliveryRevenuePct}%`}
          sub={formatCentsCompact(t.deliveryRevenueCents)}
          emphasis
        />
        <Stat
          label="Avg delivery ticket"
          value={formatCentsCompact(t.avgDeliveryTicketCents)}
          sub={`vs ${formatCentsCompact(t.avgNonDeliveryTicketCents)} in store`}
        />
      </div>

      {trends.today == null ? null : (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-[#F0F0F0] px-4 py-3">
        <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-[#888888]">
          Today
        </span>
        <span className="whitespace-nowrap text-sm text-[#333333]">
          <span className="font-bold text-[#111111]">{trends.today.deliveries}</span> deliveries
        </span>
        <span className="whitespace-nowrap text-sm text-[#333333]">
          <span className="font-bold text-[#111111]">{trends.today.transactions.toLocaleString("en-US")}</span> orders
        </span>
        <span className="ml-auto whitespace-nowrap text-xs text-[#999999]">
          vs {t.deliveriesPerDay}/day avg
        </span>
      </div>
      )}

      {peak.hour != null ? (
        <div className="border-b border-[#F0F0F0] bg-[#F7FAF9] px-4 py-3">
          <p className="text-sm text-[#333333]">
            Busiest at <span className="font-bold text-green">{formatHourRange(peak.hour)}</span> —{" "}
            {peak.pctOfDeliveries}% of deliveries.
            {peak.blockStartHour != null ? (
              <>
                {" "}
                <span className="font-medium text-[#111111]">
                  {formatHourRange(peak.blockStartHour, peak.blockHours)}
                </span>{" "}
                carries {peak.blockPctOfDeliveries}%.
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      <div className="px-4 py-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[#888888]">
          Deliveries by hour
        </p>
        <HourCurve trends={trends} />
      </div>

      <div className="border-t border-[#F0F0F0] px-4 py-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[#888888]">
          Deliveries by day of week
        </p>
        <DayOfWeekBars trends={trends} />
      </div>

      <div className="border-t border-[#F0F0F0] px-4 py-4">
        <div className="mb-2 flex items-baseline justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-[#888888]">Daily volume</p>
          <p className="text-xs text-[#999999]">{trends.window.days}-day trend</p>
        </div>
        <DailySparkline trends={trends} />
      </div>
    </div>
  );
}
